import type { QueryClient } from "@tanstack/react-query";

import { api } from "../../api/client";
import type { ExampleExercise } from "../../api/types";
import { lang, t } from "../../i18n/i18n";
import { licenceStore } from "../../state/licenceScope";

/** Premiers pas de l'accueil : ce que ce poste retient (étapes masquées, temps de la carte déjà parcourus), et
 *  le dossier d'exemple (prise en main étoffée, demande d'Alexis du 01/10/2026). */

const HIDDEN_KEY = "generex:premiers-pas:masques";
const PHASES_KEY = "generex:premiers-pas:temps";

// Rangés sous la licence (audit de persistance m8) : les premiers pas d'une licence ne masquent pas ceux d'une
// autre sur un navigateur partagé. Stockage indisponible : l'étape sera reproposée.
const read = (key: string): boolean => licenceStore.get(key) === "1";
const write = (key: string): void => licenceStore.set(key, "1");

export const firstStepsHidden = () => read(HIDDEN_KEY);
export const hideFirstSteps = () => write(HIDDEN_KEY);
/** L'utilisateur a déjà changé de temps sur la carte (T1, T2, T3), sur ce poste. */
export const phasesTried = () => read(PHASES_KEY);
export const markPhasesTried = () => write(PHASES_KEY);

/** Dossier d'exemple tout prêt : régiment d'infanterie, mission « S'emparer » (sinon la première), camp de
 *  Mourmelon (sinon le premier), ennemi TITAN, dans la langue de l'interface. Le serveur y retouche le fuseau et
 *  choisit la mission d'une unité (vue Manœuvre, docs/14 §2.12). */
export async function createExampleDossier(queryClient: QueryClient): Promise<ExampleExercise> {
  const [forces, maps] = await Promise.all([api.forces("REGIMENT"), api.maps()]);
  const force = forces.find((f) => f.family === "INF" && !f.user) ?? forces.find((f) => !f.user) ?? forces[0];
  const camps = maps.filter((m) => m.kind !== "user");
  const camp = camps.find((m) => m.slug === "camp-de-mourmelon") ?? camps[0];
  if (!force || !camp) throw new Error(t("Aucun ordre de bataille ou aucun camp sur le serveur."));
  const missions = await api.missions("REGIMENT", force.ref);
  const mission = missions.find((m) => m.name === "S'emparer") ?? missions[0];
  const ex = await api.createExampleExercise({
    name: t("Dossier d'exemple · {force}", { force: force.label }),
    language: lang(),
    map_slug: camp.slug,
    parameters: {
      level: "REGIMENT",
      odb: force.ref,
      mission: mission?.name ?? null,
      mode_action: mission?.mode ?? null,
      enemy_type: "Titan",
    },
  });
  await queryClient.invalidateQueries({ queryKey: ["exercises"] });
  return ex;
}

/** Avis d'ouverture du dossier d'exemple : la vue Manœuvre n'est citée que pour les retouches réellement faites
 *  par le serveur (une retouche refusée est omise sans bruit côté serveur). */
export function exampleDetail(example: ExampleExercise["example"] | undefined): string {
  if (example?.corridor && example.mission) {
    return t("Parcourez T1, T2, T3 en bas de l'écran ; la vue Manœuvre montre son fuseau retouché et la mission choisie.");
  }
  if (example?.corridor) return t("Parcourez T1, T2, T3 en bas de l'écran ; la vue Manœuvre montre son fuseau retouché.");
  if (example?.mission) return t("Parcourez T1, T2, T3 en bas de l'écran ; la vue Manœuvre montre la mission choisie.");
  return t("Parcourez T1, T2, T3 en bas de l'écran.");
}
